import { useMountEffect } from "@react-hookz/web";
import { isEqual } from "lodash-es";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Location, useLocation } from "react-router";
import UserAuthenticationContext, {
  UserAuthenticationContextState,
  UserAuthenticationContextValue,
} from "~/contexts/UserAuthenticationContext";

export type UserAuthenticationProviderProps = {
  children: ReactNode;
};

const userAccessTokenLocalStorageKey = "userAccessToken";

export default function UserAuthenticationProvider({ children }: UserAuthenticationProviderProps) {
  const location = useLocation();
  const previousLocation = useRef<Location>(undefined);
  const [state, setState] = useState<UserAuthenticationContextState>({ isTransitioning: false });
  const previousUserAccessToken = useRef<UserAccessToken | null>(undefined);

  const setUser = useCallback((user?: HydratedAuthenticationUser | null) => {
    setState((s) => ({ ...s, user }));
  }, []);

  const setUserAccessToken = useCallback((userAccessToken: UserAccessToken | null) => {
    setState((s) => ({ ...s, isTransitioning: true, userAccessToken }));
  }, []);

  const value = useMemo<UserAuthenticationContextValue>(() => {
    return {
      ...state,
      set: setState,
      setUser,
      setUserAccessToken,
    };
  }, [setUser, setUserAccessToken, state]);

  useMountEffect(() => {
    const localStorageData = localStorage.getItem(userAccessTokenLocalStorageKey);

    let userAccessToken: UserAccessToken | null = null;

    if (localStorageData !== null) {
      try {
        userAccessToken = JSON.parse(localStorageData);
      } catch (_) {
        // user access token data in the local storage is not valid, ignore it.
      }
    }

    setState((s) => ({ ...s, userAccessToken }));
  });

  useEffect(() => {
    if (state.userAccessToken !== undefined) {
      if (!isEqual(state.userAccessToken, previousUserAccessToken.current)) {
        if (state.userAccessToken === null) {
          window.localStorage.removeItem(userAccessTokenLocalStorageKey);
        } else {
          window.localStorage.setItem(userAccessTokenLocalStorageKey, JSON.stringify(state.userAccessToken));
        }
      }

      previousUserAccessToken.current = state.userAccessToken;
    }
  }, [previousUserAccessToken, state.userAccessToken]);

  useEffect(() => {
    if (!isEqual(location, previousLocation.current)) {
      const isTransitioning = (state.user === null && state.userAccessToken !== null)
        || (state.user !== null && state.userAccessToken === null);

      setState((s) => ({ ...s, isTransitioning }));

      previousLocation.current = location;
    }
  }, [location, state.user, state.userAccessToken]);

  return (
    <UserAuthenticationContext.Provider value={value}>
      {children}
    </UserAuthenticationContext.Provider>
  );
}
